import React, { useEffect } from "react";
import { formatNumber } from "@/util/sums";
import ColFormValueSlider from "./ColFormValueSlider";
import { useController, useFormContext, useWatch } from "react-hook-form";
import ControlledFormField from "@/model/interface/ControllerRangeSlider";
import clamp from "@/util/clamp";
import { CalculatorField } from "@/model/interface/CalculatorTypes";

const getDepositEstValueFields = (depsArray: number[]) => {
  const [homeValue] = depsArray;

  const minAmount = homeValue * 0.1;
  const maxAmount = homeValue * 0.2;
  const newMortgageValue = homeValue * 0.85;
  const newDepositValue = homeValue - newMortgageValue;

  return {
    defaultValue: 2500000,
    minAmount,
    maxAmount,
    value: clamp(newDepositValue, minAmount, maxAmount),
    rightValue: newMortgageValue,
  };
};

const getRightSideValue = (value: number, depsArray: number[]) => {
  const [homeValue] = depsArray;
  return homeValue - value;
};

const fieldName = CalculatorField.DepositValue;
const fieldWatched = CalculatorField.HomeValue;
const DepositEstimatedValue: React.FC<ControlledFormField> = ({ fieldPrefix }) => {
  const targetName = [fieldPrefix, fieldName].filter(Boolean).join(".");
  const dependency = [fieldPrefix, fieldWatched].filter(Boolean).join(".");

  const { control, setValue } = useFormContext();
  const depsArray = useWatch({ control, name: [dependency] });

  const { value, rightValue, defaultValue, ...minMaxParams } = getDepositEstValueFields(depsArray);

  const { field } = useController({
    control,
    name: targetName,
    defaultValue,
  });

  useEffect(() => {
    if (!value) return;
    setValue(targetName, value);
  }, [depsArray]);

  return (
    <ColFormValueSlider
      title="Deposit value estimate"
      step={1000}
      value={field.value}
      {...minMaxParams}
      leftColumnValue={field.value}
      leftColumnLabel="Deposit value"
      leftColumnFormatFunction={(value) => `$${formatNumber(value, 0, true)}`}
      rightColumnValue={getRightSideValue(field.value, depsArray)}
      rightColumnLabel="Mortgage value"
      rightColumnFormatFunction={(value) => `$${formatNumber(value, 0, true)}`}
      onChange={field.onChange}
      onFinalChange={field.onChange}
    />
  );
};

export default DepositEstimatedValue;
